/**
 * What a notification says, in the reader's language.
 *
 * The server stores every notification's text in English. Types with a translation
 * here are rendered from it, so a Georgian reader gets Georgian; anything else shows
 * the stored message as before.
 */
const TRANSLATED = {
  kyb_approved: { key: "notifications.kybApproved" },
  kyb_declined: { key: "notifications.kybDeclined" },
  // A person's own check before withdrawing is no longer asked for; these keep the
  // decisions already sent readable.
  kyc_approved: { key: "notifications.kycApproved" },
  kyc_declined: { key: "notifications.kycDeclined" },
  // Refunds. Ones sent before they named the product keep their stored words.
  refund_requested: { key: "notifications.refundRequested", needs: "productName" },
  refund_rejected: { key: "notifications.refundRejected", needs: "productName" },
  purchase_refunded: { key: "notifications.purchaseRefunded", needs: "productName" },
  referral_joined: { key: "notifications.referralJoined", needs: "username" },
  comment_reply: { key: "notifications.commentReply", needs: "username" },
  comment_mention: { key: "notifications.commentMention", needs: "username" },
  subscription_ended_space_deleted: {
    key: "notifications.subscriptionEndedSpaceDeleted",
    needs: "productName",
  },
  // A seller's sales. Ones sent before they named the buyer keep their stored words.
  product_sold: {
    key: (meta) => (meta.promoCode ? "notifications.productClaimed" : "notifications.productSold"),
    needs: "buyerName",
  },
  // The seller's copy; the buyer's own has no buyerName and keeps its stored words.
  trial_started: { key: "notifications.trialStartedForSeller", needs: "buyerName" },
  affiliate_added: { key: "notifications.affiliateAdded", needs: "productName" },
  // What happens around someone. @see mintly-back/src/services/activityNotifications.js
  mint_space_post: {
    key: (meta) => (meta.snippet ? "notifications.mintSpacePost" : "notifications.mintSpacePostBare"),
    needs: "businessName",
  },
  post_comment: { key: "notifications.postComment", needs: "username" },
  submission_received: { key: "notifications.submissionReceived", needs: "actorName" },
  submission_approved: { key: "notifications.submissionApproved", needs: "campaignName" },
  submission_rejected: { key: "notifications.submissionRejected", needs: "campaignName" },
  submission_appealed: { key: "notifications.submissionAppealed", needs: "actorName" },
  creator_earnings: { key: "notifications.creatorEarnings", needs: "campaignName" },
  campaign_budget_used_up: { key: "notifications.campaignBudgetUsedUp", needs: "campaignName" },
  campaign_funded: { key: "notifications.campaignFunded", needs: "amount" },
  join_requested: { key: "notifications.joinRequested", needs: "actorName" },
  join_request_accepted: { key: "notifications.joinRequestAccepted", needs: "businessName" },
  join_request_rejected: { key: "notifications.joinRequestRejected", needs: "businessName" },
  member_joined: { key: "notifications.memberJoined", needs: "actorName" },
  consulting_booked: { key: "notifications.consultingBooked" },
  consulting_reminder: { key: "notifications.consultingReminder" },
  role_assigned: { key: "notifications.roleAssigned", needs: "role" },
  role_removed: { key: "notifications.roleRemoved", needs: "businessName" },
  affiliate_joined: { key: "notifications.affiliateJoined", needs: "actorName" },
  affiliate_applied: { key: "notifications.affiliateApplied", needs: "actorName" },
  affiliate_approved: { key: "notifications.affiliateApproved", needs: "productName" },
  affiliate_rejected: { key: "notifications.affiliateRejected", needs: "productName" },
  affiliate_disabled: { key: "notifications.affiliateDisabled", needs: "productName" },
  affiliate_commission: { key: "notifications.affiliateCommission", needs: "amount" },
  subscription_payment_received: { key: "notifications.subscriptionPaymentReceived", needs: "buyerName" },
  sale_refunded: { key: "notifications.saleRefunded", needs: "buyerName" },
  payout_requested: { key: "notifications.payoutRequested", needs: "amount" },
  topup_completed: { key: "notifications.topupCompleted", needs: "amount" },
  space_wallet_withdrawn: { key: "notifications.spaceWalletWithdrawn", needs: "actorName" },
  space_wallet_deposited: { key: "notifications.spaceWalletDeposited", needs: "actorName" },
  product_reviewed: { key: "notifications.productReviewed", needs: "actorName" },
  new_product: { key: "notifications.newProduct", needs: "productName" },
  new_follower: { key: "notifications.newFollower", needs: "actorName" },
  followed_new_business: { key: "notifications.followedNewBusiness", needs: "businessName" },
  // Opens /support. @see mintly-back/src/services/supportChat.js
  support_reply: { key: "notifications.supportReply" },
  // Opens the group. @see mintly-back/src/controllers/groupChatController.js
  group_added: { key: "notifications.groupAdded", needs: "actorName" },
  // Opens nothing: the group is gone for them.
  group_removed: { key: "notifications.groupRemoved", needs: "actorName" },
  // One per chat until it's read. @see mintly-back/src/services/chatNotifications.js
  // A group message that @mentions them; opens the group.
  chat_mention: { key: "notifications.chatMention", needs: "actorName" },
  consulting_session_started: { key: "notifications.consultingSessionStarted", needs: "productName" },
  consulting_message: { key: "notifications.consultingMessage", needs: "actorName" },
  consulting_call: { key: "notifications.consultingCall", needs: "actorName" },
  chat_messages: {
    key: (meta) => (meta.groupName ? "notifications.chatMessagesGroup" : "notifications.chatMessagesDirect"),
    needs: "actorName",
  },
};

const money = (amount, currency = "GEL") =>
  `${currency === "GEL" ? "₾" : `${currency} `}${Number(amount || 0).toFixed(2)}`;

export function notificationText(notification, t) {
  const entry = TRANSLATED[notification?.type];
  const meta = notification?.meta || {};
  if (!entry || (entry.needs && !meta[entry.needs])) return notification?.message || "";
  const key = typeof entry.key === "function" ? entry.key(meta) : entry.key;
  const text = t(key, {
    name: meta.businessName || "",
    product: meta.productName || "",
    buyer: meta.buyerName || "",
    username: meta.username || "",
    code: meta.promoCode || "",
    days: meta.trialDays ?? "",
    actor: meta.actorName || "",
    group: meta.groupName || "",
    campaign: meta.campaignName || "",
    snippet: meta.snippet || "",
    rating: meta.rating ?? "",
    role: meta.role ? t(`mintSpaceSettings.teamRole_${meta.role}`) : "",
    amount: meta.amount != null ? money(meta.amount, meta.currency) : "",
  });
  return meta.reason ? `${text} ${t("notifications.reason", { reason: meta.reason })}` : text;
}

/** Where opening a notification should take the reader, if anywhere. Only our own pages. */
export function notificationLink(notification) {
  const meta = notification?.meta || {};
  if (meta.conversationId) return `/messages/${meta.conversationId}`;
  const sale = saleLink(notification);
  if (sale) return sale;
  if (meta.mintSpaceId && meta.engagementId && meta.instanceId) {
    return `/mint-space/${meta.mintSpaceId}?nav=instance-${encodeURIComponent(
      meta.instanceId
    )}&engagement=${meta.engagementId}`;
  }
  const link = typeof meta.link === "string" ? meta.link : "";
  if (link.startsWith("/") && !link.startsWith("//")) return link;
  return null;
}

/** What a seller hears about a sale: the sale itself, a renewal of it, or its refund. */
const SELLER_SALE_TYPES = new Set([
  "product_sold",
  "trial_started",
  "subscription_payment_received",
  "sale_refunded",
]);

/**
 * A seller's sale notice opens that sale in their space's Sales list. Ones sent before
 * they carried the sale open that buyer's purchases there, and the oldest, which didn't
 * name the buyer, the whole list. The buyer's own "trial started" notice names no buyer
 * and isn't one of these.
 */
function saleLink(notification) {
  const meta = notification?.meta || {};
  if (!SELLER_SALE_TYPES.has(notification?.type) || !meta.mintSpaceId) return null;
  const params = new URLSearchParams({ nav: "settings", section: "sales" });
  if (meta.purchaseId) params.set("purchase", meta.purchaseId);
  else if (meta.buyerId) params.set("buyer", meta.buyerId);
  else if (notification.type !== "product_sold") return null;
  return `/mint-space/${meta.mintSpaceId}?${params}`;
}
